import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, Typography } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils';
import { formatGel } from '../../sellerMeta';
import type { DiscountPreview } from './useDiscountDialog';

type DiscountDialogPreviewProps = {
    preview: DiscountPreview;
    /** `line` names the new figure "Line total", `sale` "Sale total". */
    kind: 'line' | 'sale';
};

/**
 * The live preview: the discount amount, then the new line or sale total
 * with the figure it came from struck through beside it. A refused value
 * shows both as unknown; nothing typed shows the figure as it stands. The
 * discount is in the default foreground (the warning-soft colour fails
 * contrast in dark themes); the minus sign carries the meaning.
 */
export const DiscountDialogPreview: React.FC<DiscountDialogPreviewProps> = ({
    preview,
    kind,
}) => {
    const { t } = useTranslation('pages');
    const unknown = t('seller.discount.unknown');
    const fits = preview.status === 'fits';

    return (
        <Card
            className="gap-1"
            {...getNodeTestIdProp('seller-discount-preview')}
            variant="secondary"
        >
            <div className="flex items-center justify-between">
                <Typography color={fits ? undefined : 'muted'} type="body-sm">
                    {t('seller.discount.previewDiscount')}
                </Typography>
                <Typography
                    className="tabular-nums"
                    color={fits ? undefined : 'muted'}
                    {...getNodeTestIdProp('seller-discount-preview-amount')}
                    type="body-sm"
                >
                    {fits ? `−${formatGel(preview.discount)}` : unknown}
                </Typography>
            </div>
            <div className="flex items-center justify-between">
                <Typography type="body-sm" weight="semibold">
                    {t(
                        kind === 'line'
                            ? 'seller.discount.lineTotal'
                            : 'seller.discount.saleTotal',
                    )}
                </Typography>
                <div className="flex items-baseline gap-2 tabular-nums">
                    {fits ? (
                        <Typography
                            className="line-through"
                            color="muted"
                            {...getNodeTestIdProp(
                                'seller-discount-preview-base',
                            )}
                            type="body-xs"
                        >
                            {formatGel(preview.base)}
                        </Typography>
                    ) : null}
                    <Typography
                        {...getNodeTestIdProp('seller-discount-preview-total')}
                        type="body"
                        weight="bold"
                    >
                        {preview.status === 'fits'
                            ? formatGel(preview.total)
                            : preview.status === 'empty'
                              ? formatGel(preview.base)
                              : unknown}
                    </Typography>
                </div>
            </div>
        </Card>
    );
};
